/**
 * OnlineSupportFocusModal — full-screen focus view for a single
 * OnlineSupport item. Mirrors FindingFocusModal's two-column layout,
 * adapted for online-search results (no embedded article iframe;
 * external sites typically block iframing, and the value of the focus
 * view here is laying out summary + report + every reference clearly
 * for triangulation).
 *
 * Opened from the "Explore deeper" action in the OnlineSupportPanel
 * detail header. Esc, X, or backdrop click all close.
 *
 * Layout:
 *   - Primary  (left, ~62%): rationale, summary, full report (explore
 *     mode), source pointer, affirmative source, primary external URL
 *     CTA, linked node.
 *   - Secondary (right, ~38%): scannable rail of all references with
 *     snippets + per-reference Open-source links; status pills;
 *     metadata; retrieval timestamp.
 *
 * This view is additive: the inline master/detail panel is untouched.
 */

import React, { useMemo } from 'react';
import { formatDateTime } from '../../utils/formatDateTime';
import { formatCount } from '../../utils/plural';
import {
  ArrowUpRight,
  BookOpen,
  CheckCircle,
  Clock,
  ExternalLink,
  Globe2,
  Hash,
  Link2,
  Search,
  X,
} from 'lucide-react';
import type { DictNode } from '../../types/workspaceState';
import type { OnlineReference, OnlineSupport } from '../../types/online_support';
import { isSafeUrl } from '../../utils/urlSafety';
import DialogShell from '../ui/DialogShell';

interface OnlineSupportFocusModalProps {
  isOpen: boolean;
  support: OnlineSupport;
  nodes?: Record<string, DictNode>;
  onClose: () => void;
  onNavigateToNode?: (nodeId: string) => void;
}

function formatDate(value?: string | null): string {
  if (!value) return '';
  return formatDateTime(value, { invalidFallback: value });
}

function formatLabel(value: string): string {
  return value.replace(/_/g, ' ');
}

function hostFromUrl(url?: string | null): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function FocusReferenceCard({ reference }: { reference: OnlineReference }) {
  const title = reference.title || hostFromUrl(reference.url) || reference.url;
  const domain = reference.domain || hostFromUrl(reference.url);
  // Only http(s) URLs become live links; anything else renders inert.
  const safeUrl = isSafeUrl(reference.url) ? reference.url : null;

  return (
    <div className="rounded-lg border border-border-default bg-surface px-3 py-2.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {safeUrl ? (
            <a
              href={safeUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open: ${safeUrl}`}
              className="text-sm font-medium text-link hover:underline inline-flex items-center gap-1.5 min-w-0 max-w-full"
            >
              <span className="truncate">{title}</span>
              <ExternalLink size={11} strokeWidth={1.5} className="flex-shrink-0" />
            </a>
          ) : (
            <p className="text-sm font-medium text-fg truncate">{title}</p>
          )}
          {domain && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-xs px-1.5 py-0.5 rounded bg-bg-warm text-fg-tertiary">
                {domain}
              </span>
              {reference.published_date && (
                <span className="text-xs text-fg-tertiary">
                  Published {reference.published_date}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {reference.snippet && (
        <p className="mt-2 text-xs text-fg-secondary leading-relaxed whitespace-pre-wrap">
          {reference.snippet}
        </p>
      )}
      {reference.why_relevant && (
        <div className="mt-2 pt-2 border-t border-divider">
          <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-0.5">
            Why it mattered
          </div>
          <p className="text-xs text-fg-secondary leading-relaxed">
            {reference.why_relevant}
          </p>
        </div>
      )}
    </div>
  );
}

export default function OnlineSupportFocusModal({
  isOpen,
  support,
  nodes,
  onClose,
  onNavigateToNode,
}: OnlineSupportFocusModalProps) {
  const linkedNode = support.linked_node_id && nodes
    ? nodes[support.linked_node_id]
    : null;

  const references = support.references || [];
  // http(s)-only; a non-http(s) primary candidate is dropped so it never
  // renders as a clickable anchor.
  const primaryCandidate = support.external_url || references[0]?.url || null;
  const primarySourceUrl = isSafeUrl(primaryCandidate) ? primaryCandidate : null;

  const metadataRows = useMemo(() => ([
    { label: 'Purpose', value: formatLabel(support.purpose) },
    { label: 'Status', value: formatLabel(support.status) },
    { label: 'Query', value: support.query },
    { label: 'Retrieved', value: formatDate(support.retrieved_at) },
    { label: 'Pinned', value: formatDate(support.pinned_at) },
    { label: 'External host', value: support.external_host },
    { label: 'BARK document', value: support.bark_document_id },
  ].filter((row) => row.value)), [support]);

  if (!isOpen) return null;

  return (
    <DialogShell
      open={isOpen}
      onClose={onClose}
      ariaLabel="Online support focus view"
      backdropPolicy="dismiss"
      overlayClassName="flex items-center justify-center p-4 md:p-8"
      panelClassName="w-full max-w-7xl h-full max-h-[92vh] flex flex-col overflow-hidden border border-border-default"
    >
        {/* Header */}
        <div className="px-5 py-3 flex items-center justify-between border-b border-border-default bg-bg-warm flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-surface text-fg-secondary text-xs font-semibold flex-shrink-0">
              <Globe2 size={12} strokeWidth={1.5} />
              Online support
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-surface text-fg-tertiary text-xs flex-shrink-0">
              <Search size={11} strokeWidth={1.5} />
              {formatLabel(support.purpose)}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-surface text-fg-tertiary text-xs flex-shrink-0">
              <CheckCircle size={11} strokeWidth={1.5} />
              {formatLabel(support.status)}
            </span>
            <span className="text-sm font-semibold text-fg truncate ml-1">
              {support.rationale || support.query}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-surface rounded transition-colors flex-shrink-0 ml-3"
            title="Close (Esc)"
            aria-label="Close focus view"
          >
            <X size={18} strokeWidth={1.5} className="text-fg-tertiary" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 flex min-h-0 overflow-hidden">
          {/* Primary column */}
          <div className="flex-[0_0_62%] min-w-0 overflow-y-auto border-r border-border-default p-5 space-y-5">
            {support.rationale && (
              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1.5">
                  Rationale for keeping this support
                </div>
                <p className="text-sm text-fg leading-relaxed whitespace-pre-wrap">
                  {support.rationale}
                </p>
              </section>
            )}

            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1.5">
                Search summary
              </div>
              <p className="text-sm text-fg-secondary leading-relaxed whitespace-pre-wrap">
                {support.summary}
              </p>
            </section>

            {support.report && (
              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1.5">
                  Synthesised report
                </div>
                <div className="rounded-lg border border-border-default bg-surface-sunken p-4">
                  <p className="text-sm text-fg-secondary leading-relaxed whitespace-pre-wrap">
                    {support.report}
                  </p>
                </div>
              </section>
            )}

            {(support.source_pointer || support.affirmative_source) && (
              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1.5">
                  Source evidence
                </div>
                <div className="rounded-lg border border-border-default bg-surface-sunken p-4 space-y-3">
                  {support.source_pointer && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1">
                        Source pointer
                      </div>
                      <p className="text-xs text-fg-secondary leading-relaxed whitespace-pre-wrap">
                        {support.source_pointer}
                      </p>
                    </div>
                  )}
                  {support.affirmative_source && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1">
                        Affirmative source
                      </div>
                      <p className="text-xs text-fg-secondary leading-relaxed whitespace-pre-wrap">
                        {support.affirmative_source}
                      </p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {primarySourceUrl && (
              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1.5">
                  Primary source
                </div>
                <a
                  href={primarySourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-link/40 bg-surface text-link hover:bg-link/5 transition-colors text-sm font-medium"
                  title={`Open: ${primarySourceUrl}`}
                >
                  <span className="truncate max-w-[60ch]">{primarySourceUrl}</span>
                  <ArrowUpRight size={14} strokeWidth={1.5} className="flex-shrink-0" />
                </a>
                {support.external_host && (
                  <span className="ml-2 text-xs text-fg-tertiary">
                    via {support.external_host}
                  </span>
                )}
              </section>
            )}

            {linkedNode && support.linked_node_id && (
              <section>
                <div className="text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-1.5">
                  Linked evidence node
                </div>
                <button
                  type="button"
                  onClick={() => {
                    onNavigateToNode?.(support.linked_node_id!);
                    onClose();
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border-default bg-surface text-left hover:border-border-strong transition-colors"
                >
                  <Link2 size={14} strokeWidth={1.5} className="text-fg-tertiary flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-fg-secondary truncate">{linkedNode.label}</p>
                    <p className="text-xs text-fg-tertiary">Open node in workspace</p>
                  </div>
                </button>
              </section>
            )}
          </div>

          {/* Secondary column — references rail */}
          <div className="flex-1 min-w-0 overflow-y-auto p-5 bg-surface-sunken">
            <div className="space-y-5">
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-fg-tertiary">
                    <BookOpen size={12} strokeWidth={1.5} />
                    References
                  </div>
                  <span className="text-xs text-fg-tertiary">
                    {formatCount(references.length, { one: 'source', other: 'sources' })}
                  </span>
                </div>
                {references.length > 0 ? (
                  <div className="space-y-2">
                    {references.map((reference) => (
                      <FocusReferenceCard
                        // Stable, index-independent key. url is the natural id,
                        // but it can be blanked by the URL-safety filter, so
                        // fall back to a title+url composite to stay unique-ish
                        // without the array index.
                        key={reference.url || `${reference.title}-${reference.url}`}
                        reference={reference}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-lg border border-border-default bg-surface p-4 text-center">
                    <p className="text-sm text-fg-tertiary">
                      No web references were saved for this support item.
                    </p>
                  </div>
                )}
              </section>

              <section>
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-fg-tertiary mb-2">
                  <Hash size={12} strokeWidth={1.5} />
                  Metadata
                </div>
                <dl className="rounded-lg border border-border-default bg-surface p-3 space-y-2 text-xs">
                  {metadataRows.map((row) => (
                    <div key={row.label} className="grid grid-cols-3 gap-3">
                      <dt className="text-fg-tertiary">{row.label}</dt>
                      <dd className="text-fg-secondary min-w-0 break-words col-span-2">{row.value}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <div className="flex items-center gap-1.5 text-xs text-fg-tertiary">
                <Clock size={12} strokeWidth={1.5} />
                Retrieved {formatDate(support.retrieved_at) || 'unknown time'}
              </div>
            </div>
          </div>
        </div>
    </DialogShell>
  );
}
